import React, { useState, useEffect } from 'react';
import { FetchFeaturedProducts } from '../../Data';
import ModernAdBanner from './ModernAdBanner';
import AdPopup from './AdPopup';
import StickyAdBanner from './StickyAdBanner';
import ProductAdCard from './ProductAdCard';

/**
 * AdManager - Central component for managing and displaying all types of advertisements
 */
const AdManager = ({ 
  location = 'home', // 'home', 'category', 'product', 'checkout', etc.
  userId = null,
  userPreferences = {},
  context = {}, // Additional context like category, product info
  onAdInteraction
}) => {
  const [ads, setAds] = useState({
    hero: [],
    sticky: null,
    popup: null,
    sidebar: [],
    products: [],
    strips: []
  });
  
  const [showPopup, setShowPopup] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load advertisements based on location and user preferences
  useEffect(() => {
    loadAds();
  }, [location, userId, context.category]);

  const loadAds = async () => {
    try {
      setLoading(true);
      
      // Load static ads and dynamic featured products in parallel
      const [staticAdsData, featuredProducts] = await Promise.all([
        getAdsByLocation(location, userId, userPreferences),
        loadFeaturedProducts()
      ]);
      
      // Merge static ads with dynamic featured products
      const mergedAds = {
        ...staticAdsData,
        products: featuredProducts || staticAdsData.products || []
      };
      
      setAds(mergedAds);
      
      // Show popup after page load
      if (mergedAds.popup && shouldShowPopup(mergedAds.popup)) {
        setTimeout(() => setShowPopup(true), 2000);
      }
      
      // Show sticky banner
      if (mergedAds.sticky && shouldShowSticky(mergedAds.sticky)) {
        setShowSticky(true);
      }
      
    } catch (error) {
      console.error('Error loading ads:', error);
      // Fallback to static ads only
      const fallbackAds = await getAdsByLocation(location, userId, userPreferences);
      setAds(fallbackAds);
    } finally {
      setLoading(false);
    }
  };

  const loadFeaturedProducts = async () => {
    try {
      const limit = 10;
      const category = context.category || null;
      
      const featuredProducts = await FetchFeaturedProducts(limit, category);
      
      // Transform featured products to match ProductAdCard format
      return featuredProducts.map(product => ({
        id: product.productId || product.id,
        imageUrl: Array.isArray(product.imageUrls) ? product.imageUrls[0] : product.imageUrl,
        title: product.productName || product.title,
        brand: product.brand || product.merchant?.merchantName || 'Brand',
        price: product.price,
        originalPrice: product.discount > 0 ? product.price / (1 - product.discount / 100) : product.price,
        rating: product.rating || 4.5,
        reviewCount: product.reviewCount || product.reviews || Math.floor(Math.random() * 1000) + 100,
        description: product.description || product.productDescription,
        productUrl: `/product/${encodeURIComponent(product.productName || product.title)}/${product.productId || product.id}`,
        isNew: product.isNew || product.isFeatured,
        isFeatured: product.isFeatured,
        stock: product.stockQuantity || product.stock || 10,
        discount: product.discount || 0
      }));
    } catch (error) {
      console.error('Error loading featured products:', error);
      return [];
    }
  };

  const shouldShowPopup = (popupAd) => {
    // Check if popup was already shown today
    const lastShown = localStorage.getItem(`popup_${popupAd.id}_last_shown`);
    const today = new Date().toDateString();
    
    return !lastShown || lastShown !== today;
  };

  const shouldShowSticky = (stickyAd) => {
    // Check if sticky banner was dismissed
    const dismissed = localStorage.getItem(`sticky_${stickyAd.id}_dismissed`);
    return !dismissed;
  };

  const handleAdInteraction = (adType, action, ad) => {
    // Track ad interactions
    if (onAdInteraction) {
      onAdInteraction({
        adType,
        action,
        ad,
        location,
        timestamp: new Date().toISOString(),
        userId
      });
    }
    
    // Handle specific actions
    switch (action) {
      case 'click':
        trackAdClick(ad);
        break;
      case 'view':
        trackAdView(ad);
        break;
      case 'close':
        handleAdClose(adType, ad);
        break;
      default:
        break;
    }
  };

  const trackAdClick = (ad) => {
    // Increment click counter
    const clicks = parseInt(localStorage.getItem(`ad_${ad.id}_clicks`) || '0') + 1;
    localStorage.setItem(`ad_${ad.id}_clicks`, clicks.toString());
  };

  const trackAdView = (ad) => {
    // Increment view counter
    const views = parseInt(localStorage.getItem(`ad_${ad.id}_views`) || '0') + 1;
    localStorage.setItem(`ad_${ad.id}_views`, views.toString());
  };

  const handleAdClose = (adType, ad) => {
    if (adType === 'popup') {
      setShowPopup(false);
      localStorage.setItem(`popup_${ad.id}_last_shown`, new Date().toDateString());
    } else if (adType === 'sticky') {
      setShowSticky(false);
      localStorage.setItem(`sticky_${ad.id}_dismissed`, 'true');
    }
  };

  const handlePopupAction = (action, ad) => {
    handleAdInteraction('popup', action, ad);
    setShowPopup(false);
    
    // Mark popup as shown for today
    localStorage.setItem(`popup_${ad.id}_last_shown`, new Date().toDateString());
  };

  const handleStickyClose = () => {
    if (ads.sticky) {
      handleAdInteraction('sticky', 'close', ads.sticky);
    }
  };

  if (loading) {
    return <AdLoadingPlaceholder />;
  }

  return (
    <div className="ad-manager">
      {/* Hero/Main Banner */}
      {ads.hero && ads.hero.length > 0 && (
        <div className="mb-6">
          <ModernAdBanner
            banners={ads.hero}
            type="hero"
            autoPlay={true}
            showControls={true}
            showIndicators={true}
            onBannerClick={(banner) => handleAdInteraction('hero', 'click', banner)}
            onBannerView={(banner) => handleAdInteraction('hero', 'view', banner)}
          />
        </div>
      )}

      {/* Strip Banners */}
      {ads.strips && ads.strips.length > 0 && (
        <div className="space-y-4 mb-6">
          {ads.strips.map((strip, index) => (
            <ModernAdBanner
              key={strip.id || index}
              banners={[strip]}
              type="strip"
              autoPlay={false}
              showControls={false}
              showIndicators={false}
              onBannerClick={(banner) => handleAdInteraction('strip', 'click', banner)}
            />
          ))}
        </div>
      )}

      {/* Product Ads Grid - Featured Products */}
      {ads.products && ads.products.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">
              {context.category ? `Featured ${context.category} Products` : 'Featured Products'}
            </h3>
            <span className="text-sm text-gray-500">
              {ads.products.length} products available
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {ads.products
              .filter(product => product.stock > 0) // Only show in-stock products
              .slice(0, 10)
              .map((product, index) => (
                <ProductAdCard
                  key={product.id || index}
                  ad={product}
                  size="medium"
                  onProductClick={(product) => handleAdInteraction('featured_product', 'click', product)}
                  onAddToCart={(product) => handleAdInteraction('featured_product', 'add_to_cart', product)}
                  onAddToWishlist={(product) => handleAdInteraction('featured_product', 'add_to_wishlist', product)}
                />
              ))}
          </div>
          {ads.products.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>No featured products available at the moment.</p>
            </div>
          )}
        </div>
      )}

      {/* Sidebar Ads */}
      {ads.sidebar && ads.sidebar.length > 0 && (
        <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-30 hidden lg:block">
          <div className="space-y-4">
            {ads.sidebar.map((sidebarAd, index) => (
              <ModernAdBanner
                key={sidebarAd.id || index}
                banners={[sidebarAd]}
                type="sidebar"
                autoPlay={false}
                showControls={false}
                showIndicators={false}
                onBannerClick={(banner) => handleAdInteraction('sidebar', 'click', banner)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Sticky Banner */}
      {showSticky && ads.sticky && (
        <StickyAdBanner
          ad={ads.sticky}
          position="top"
          isVisible={showSticky}
          onClose={handleStickyClose}
          onAction={(ad) => handleAdInteraction('sticky', 'click', ad)}
          autoHide={ads.sticky.autoHide}
          hideAfter={ads.sticky.hideAfter}
        />
      )}

      {/* Popup Ad */}
      {showPopup && ads.popup && (
        <AdPopup
          ad={ads.popup}
          isVisible={showPopup}
          onClose={() => handlePopupAction('close', ads.popup)}
          onAction={(action, ad) => handlePopupAction(action, ad)}
          showAfter={ads.popup.showAfter || 3000}
        />
      )}
    </div>
  );
};

/**
 * Loading placeholder while ads are being fetched
 */
const AdLoadingPlaceholder = () => (
  <div className="space-y-6">
    {/* Hero banner placeholder */}
    <div className="w-full h-64 md:h-80 lg:h-96 bg-gray-200 animate-pulse rounded-lg"></div>
    
    {/* Product cards placeholder */}
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {[...Array(10)].map((_, index) => (
        <div key={index} className="bg-gray-200 animate-pulse rounded-lg h-64"></div>
      ))}
    </div>
  </div>
);

/**
 * Mock function to get ads by location
 * Replace this with actual API call
 */
const getAdsByLocation = async (location, userId, userPreferences) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Mock data based on location
  const mockAds = {
    home: {
      hero: [
        {
          id: 'hero_1',
          type: 'image',
          imageUrl: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=600&fit=crop',
          title: 'Summer Sale Up to 70% Off',
          subtitle: 'Shop the latest trends and save big on electronics, fashion, and more',
          ctaText: 'Shop Now',
          link: '/categories/sale',
          badge: 'LIMITED TIME',
          contentPosition: 'items-center justify-start pl-8',
          overlay: { opacity: 0.4 }
        },
        {
          id: 'hero_2',
          type: 'image',
          imageUrl: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=1200&h=600&fit=crop',
          title: 'New iPhone 15 Series',
          subtitle: 'Experience the most advanced iPhone ever with titanium design',
          ctaText: 'Pre-Order Now',
          link: '/products/iphone-15',
          badge: 'NEW ARRIVAL',
          contentPosition: 'items-center justify-end pr-8',
          overlay: { opacity: 0.3 }
        }
      ],
      sticky: {
        id: 'sticky_1',
        title: 'Free Shipping on Orders Over $50',
        description: 'Limited time offer - Use code FREESHIP',
        ctaText: 'Shop Now',
        link: '/categories',
        backgroundColor: 'from-green-600 to-blue-600',
        icon: 'tag',
        autoHide: true,
        hideAfter: 15000
      },
      popup: {
        id: 'popup_1',
        type: 'image',
        imageUrl: 'https://images.unsplash.com/photo-1607083206968-13611e3d76db?w=400&h=200&fit=crop',
        title: 'Welcome to Quickcrate Express!',
        description: 'Get 15% off your first order when you sign up for our newsletter',
        ctaText: 'Get 15% Off',
        secondaryCta: 'No Thanks',
        link: '/signup',
        badge: 'FIRST TIME VISITOR',
        offer: '15% OFF',
        features: [
          'Exclusive deals and offers',
          'Early access to sales',
          'Free shipping updates'
        ],
        footer: 'Unsubscribe anytime. We respect your privacy.',
        showAfter: 5000
      },
      products: [
        {
          id: 'product_1',
          imageUrl: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=300&fit=crop',
          title: 'Premium Wireless Headphones',
          brand: 'SoundMax',
          price: 129.99,
          originalPrice: 179.99,
          rating: 4.5,
          reviewCount: 1253,
          description: 'High-quality wireless headphones with noise cancellation',
          productUrl: '/products/wireless-headphones',
          isNew: true,
          stock: 15
        },
        {
          id: 'product_2',
          imageUrl: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=300&h=300&fit=crop',
          title: 'Running Shoes Pro',
          brand: 'SportTech',
          price: 89.99,
          originalPrice: 119.99,
          rating: 4.8,
          reviewCount: 2156,
          description: 'Professional running shoes for athletes',
          productUrl: '/products/running-shoes',
          isFeatured: true,
          stock: 8
        }
      ],
    //   strips: [
    //     {
    //       id: 'strip_1',
    //       imageUrl: 'https://images.unsplash.com/photo-1607083206325-caf1edba7a0f?w=1200&h=100&fit=crop',
    //       title: 'Black Friday Deals Start Now!',
    //       link: '/black-friday',
    //       backgroundColor: 'from-black to-gray-800'
    //     }
    //   ]
    },
    category: {
      hero: [
        {
          id: 'category_hero_1',
          type: 'image',
          imageUrl: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=400&fit=crop',
          title: 'Electronics Category Sale',
          subtitle: 'Up to 50% off on smartphones, laptops, and accessories',
          ctaText: 'Explore Deals',
          link: '/categories/electronics/sale'
        }
      ],
      sidebar: [
        {
          id: 'sidebar_1',
          imageUrl: 'https://images.unsplash.com/photo-1607083206968-13611e3d76db?w=250&h=300&fit=crop',
          title: 'Trending Now',
          link: '/trending'
        }
      ]
    }
  };
  
  return mockAds[location] || mockAds.home;
};

export default AdManager;